/**
 * Compatibility wrapper for withRouter from React Router v5
 * This provides a temporary migration path for components using withRouter
 * TODO: Migrate all components to use hooks (useNavigate, useLocation, useParams) instead
 */
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';

export function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        const navigate = useNavigate();
        const location = useLocation();
        const params = useParams();
        const listenersRef = useRef([]);
        const locationRef = useRef(location);
        
        // Update location ref when location changes
        useEffect(() => {
            locationRef.current = location;
            // Call all registered listeners
            listenersRef.current.forEach((listener) => {
                listener(location, 'PUSH');
            });
        }, [location]);
        
        const history = {
            push: (path, state) => navigate(path, { state, replace: false }),
            replace: (path, state) => navigate(path, { state, replace: true }),
            goBack: () => navigate(-1),
            goForward: () => navigate(1),
            go: (n) => navigate(n),
            location,
            action: 'PUSH', // Default action
            listen: (callback) => {
                // Add listener to the array
                listenersRef.current.push(callback);
                
                // Return unsubscribe function
                return () => {
                    const index = listenersRef.current.indexOf(callback);
                    if (index > -1) {
                        listenersRef.current.splice(index, 1);
                    }
                };
            },
        };
        
        return (
            <Component
                {...props}
                history={history}
                location={location}
                match={{ params, isExact: true, path: location.pathname, url: location.pathname }}
            />
        );
    }

    ComponentWithRouterProp.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

    return ComponentWithRouterProp;
}

export default withRouter;
